import React, { useState, useCallback, useEffect } from 'react'
import AuthContext from '../context/Auth'
import AuthCognito from '../libs/aws-amplify'
import { getCurrentUser, fetchUserAttributes, fetchAuthSession, signOut } from '@aws-amplify/auth'
import { EVENTO_SESION_MUERTA } from '../libs/axios'
import capitalize from 'capitalize'
import Loading from '../components/Sections/Loading'
import Roles from '../service/Roles'
import { permisosVacios } from '../config/rbac'

// Configurar Amplify una sola vez al cargar el módulo (no en cada render)
try {
    AuthCognito()
} catch (err) {
    console.error('Error configurando Amplify:', err)
}

// Roles conocidos al compilar. Ya NO son la lista de roles válidos —eso lo decide la
// colección `roles` del backend y puede crecer desde el panel—: solo sirven para
// derivar las banderas `isAdmin`/`isVentas`/… que todavía leen algunos componentes.
// Un rol creado desde el panel simplemente no tiene bandera propia, y no la necesita:
// lo que manda es `user.permisos`.
const ROLES_CON_BANDERA = ['admin', 'coordinador', 'jefe_recinto', 'ventas', 'reporte', 'cliente']

const AuthProvider = ({ children }) => {

    const [isSignedIn, setIsSignedIn] = useState(false)
    const [loading, setLoading] = useState(true)
    const [user, setUser] = useState({})

    const setUserAttributes = useCallback((attributes) => {
        try {
            if (!attributes || typeof attributes !== 'object') {
                console.warn('setUserAttributes: attributes inválido', attributes)
                return
            }

            const {
                sub,
                email,
                name,
                'custom:role': rawRole,
                'custom:area': area,
                phone_number: phoneNumber,
                'custom:rut': rut,
                birthdate,
            } = attributes

            // Sin rol en el token se asume cliente. Un rol desconocido acá YA NO se
            // degrada a 'cliente': puede ser uno creado desde el panel, y pisarlo
            // dejaría a esa persona sin sus permisos aunque el backend sí los tenga.
            const role = rawRole || 'cliente'

            // Construir flags de rol de forma segura
            const roleFlags = {}
            if (ROLES_CON_BANDERA.includes(role)) {
                try {
                    roleFlags[`is${capitalize(role)}`] = true
                } catch (err) {
                    console.warn('Error generando roleFlag, usando isCliente por default:', err)
                    roleFlags.isCliente = true
                }
            }

            setUser({
                sub: sub || null,
                email: email || null,
                name: name || '',
                area: area || null,
                phoneNumber: phoneNumber || null,
                rut: rut || null,
                birthdate: birthdate || null,
                role,
                // Hasta que responda `/sesion/permisos` no se sabe qué puede: vacío,
                // no "todo". Los guards esperan a `permisosCargados` para no mandar a
                // nadie a /unauthorized por un dato que todavía no llegó.
                permisos: permisosVacios(),
                permisosCargados: false,
                ...roleFlags,
            })
        } catch (err) {
            console.error('Error en setUserAttributes:', err)
            // En caso de error, setear user mínimo válido para que la app no crashee
            setUser({ role: 'cliente', isCliente: true, permisos: permisosVacios(), permisosCargados: true })
        }
    }, [])

    /**
     * Trae del servidor lo que esta persona puede hacer.
     *
     * Es el reemplazo de deducir el menú del nombre del rol: el panel dibuja
     * exactamente el mismo mapa que el backend verifica en cada petición, así que ya
     * no pueden discrepar (hoy discrepan: el menú le ofrece Operaciones al jefe de
     * recinto y la ruta lo rebota).
     *
     * Si la consulta falla se marca igual como cargado, con el mapa vacío: mejor que
     * el panel diga "no tienes acceso" a que se quede girando para siempre. El
     * administrador no se ve afectado porque `can()` le responde que sí de todas formas.
     */
    const cargarPermisos = useCallback(async () => {
        try {
            const sesion = await Roles.miSesion()
            setUser(prev => ({
                ...prev,
                permisos: sesion?.permisos || permisosVacios(),
                accesoTorniquete: sesion?.accesoTorniquete === true,
                rolNombre: sesion?.rolNombre || prev.role,
                permisosCargados: true,
            }))
        } catch (err) {
            console.error('No se pudieron cargar los permisos de la sesión:', err)
            setUser(prev => ({
                ...prev,
                permisos: prev.permisos || permisosVacios(),
                permisosCargados: true,
            }))
        }
    }, [])

    const authIsSuccess = useCallback((attributes) => {
        setUserAttributes(attributes)
        setIsSignedIn(true)
        return cargarPermisos()
    }, [setUserAttributes, cargarPermisos])

    useEffect(() => {
        let cancelled = false

        const checkAuth = async () => {
            // 🧪 SOLO LOCAL — sesión de dev sin Cognito (gated por REACT_APP_LOCAL_AUTH).
            // Inyecta un admin ficticio para poder clickear POS/checkout sin login real.
            if (process.env.REACT_APP_LOCAL_AUTH === 'true') {
                await authIsSuccess({
                    sub: 'local-admin', email: 'local@test.cl', name: 'Admin Local',
                    'custom:role': 'admin', 'custom:rut': '11111111-1',
                    birthdate: '1990-01-01',
                })
                if (!cancelled) setLoading(false)
                return
            }
            try {
                await getCurrentUser()
                // Solo si el usuario está autenticado, obtener atributos
                try {
                    const attributes = await fetchUserAttributes()
                    // Se espera a los permisos ANTES de soltar el `loading`: si la app
                    // montara sin ellos, las guardas verían un mapa vacío y mandarían a
                    // /unauthorized a gente que sí tiene acceso.
                    if (!cancelled) await authIsSuccess(attributes)
                } catch (attrErr) {
                    console.warn('Error obteniendo atributos del usuario:', attrErr)
                    // Seguir como logueado SOLO si Amplify de verdad tiene tokens.
                    // Marcarlo a ciegas dejaba al usuario medio adentro: navegaba,
                    // pero cada request salía sin Authorization y todo daba 401.
                    const { tokens } = await fetchAuthSession().catch(() => ({}))
                    if (cancelled) return
                    if (tokens?.idToken) {
                        setUser({ role: 'cliente', isCliente: true, permisos: permisosVacios(), permisosCargados: true })
                        setIsSignedIn(true)
                    } else {
                        setIsSignedIn(false)
                        setUser({})
                    }
                }
            } catch (err) {
                // No hay sesión activa - comportamiento normal para usuario no logueado
                if (!cancelled) {
                    setIsSignedIn(false)
                    setUser({})
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        checkAuth()

        return () => {
            cancelled = true
        }
    }, [authIsSuccess])

    // ⚠️ checkAuth corre UNA sola vez, al montar. El refresh token dura 5 días,
    // así que una pestaña que queda abierta se pasa de largo: isSignedIn sigue
    // en true para siempre, la app monta el gate y el carrito, y cada request
    // sale sin Authorization contra un 401. En producción eso era un goteo
    // constante de ~20 AUTH401/min desde un puñado de pestañas zombis, y para
    // el usuario significaba que nada cargaba y nadie le decía por qué.
    // Cuando un 401 sobrevive al reintento con refresh, cerramos de verdad.
    useEffect(() => {
        if (process.env.REACT_APP_LOCAL_AUTH === 'true') return undefined

        let cerrando = false

        const alMorirLaSesion = async () => {
            if (cerrando) return
            cerrando = true
            try {
                // Confirmar antes de botar a nadie: un 401 suelto puede venir de
                // un permiso, no de la sesión.
                const { tokens } = await fetchAuthSession().catch(() => ({}))
                if (tokens?.idToken) return

                await signOut().catch(() => {})
                setIsSignedIn(false)
                setUser({})
            } finally {
                cerrando = false
            }
        }

        window.addEventListener(EVENTO_SESION_MUERTA, alMorirLaSesion)
        return () => window.removeEventListener(EVENTO_SESION_MUERTA, alMorirLaSesion)
    }, [])

    return (
        <AuthContext.Provider value={{ user, isSignedIn, setIsSignedIn, authIsSuccess, loading }}>
            {loading ? <Loading /> : children}
        </AuthContext.Provider>
    )
}

export default AuthProvider

/*
    Al ingresar a la aplicación primero se valida que el usuario este autenticado.
    Mientras esa validación se está procesando (en segundos), el valor de loading" es "true" y se muestra temporalmente un estado de cargando.
    Esto evita que se visualice el formulario de inicio de sesión mientras carga la página y el usuario esta autenticado.
*/
